import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { ReportTableColumn } from "../components/ReportPointsTable";
import { brand } from "../../../config/theme";

// Colores del TEMA activo (config/themes/<tema>.json), no un verde fijo: el
// mismo build sirve a varios temas y el Excel/PDF salía siempre verde Dumax.
const hexToRgb = (hex: string): [number, number, number] => {
  const h = (hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = Number.parseInt(full.slice(0, 6), 16);
  if (Number.isNaN(n)) return [116, 250, 76];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const BRAND_RGB = hexToRgb(brand.primary);
const BRAND_ARGB = `FF${BRAND_RGB.map((c) => c.toString(16).padStart(2, "0")).join("").toUpperCase()}`;

interface ExportMeta {
  title: string;
  period: string;
  fileName: string;
}

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

// Para volúmenes grandes (80k+) procesamos en chunks para no congelar el hilo
// principal. Cada chunk escribe N filas y cede un tick con setTimeout.
const EXCEL_CHUNK_SIZE = 5000;

/**
 * Nombre de la hoja de Excel a partir del título del reporte. Estaba fijo en
 * "Recorrido" (heredado del reporte de recorridos), así que el Excel de
 * accesos, tarjetas, inspecciones o registros del controlador abría una hoja
 * llamada "Recorrido". Excel acepta hasta 31 caracteres y prohíbe \ / ? * [ ] :
 */
export const sheetNameFor = (title: string): string => {
  const clean = (title || "").replace(/[\\/?*[\]:]/g, " ").replace(/\s+/g, " ").trim();
  return (clean || "Reporte").slice(0, 31);
};

export const exportHistoricToExcel = async <T extends { id: string }>(
  rows: T[],
  columns: ReportTableColumn<T>[],
  meta: ExportMeta,
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet(sheetNameFor(meta.title), {
    views: [{ showGridLines: false }],
  });

  const totalCols = columns.length;

  ws.mergeCells(1, 1, 1, totalCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = meta.title;
  titleCell.font = { bold: true, size: 16, color: { argb: BRAND_ARGB } };
  titleCell.alignment = { horizontal: "center" };

  ws.mergeCells(2, 1, 2, totalCols);
  const periodCell = ws.getCell(2, 1);
  periodCell.value = meta.period;
  periodCell.font = { italic: true, size: 10, color: { argb: "FF6B7280" } };
  periodCell.alignment = { horizontal: "center" };

  const headerRowNum = 4;
  const headerRow = ws.getRow(headerRowNum);
  columns.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.header;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: BRAND_ARGB },
    };
    cell.alignment = { horizontal: "center" };
    ws.getColumn(i + 1).width = 18;
  });

  for (let start = 0; start < rows.length; start += EXCEL_CHUNK_SIZE) {
    const end = Math.min(start + EXCEL_CHUNK_SIZE, rows.length);
    for (let rowIndex = start; rowIndex < end; rowIndex++) {
      const row = rows[rowIndex];
      const r = ws.getRow(headerRowNum + 1 + rowIndex);
      columns.forEach((col, i) => {
        r.getCell(i + 1).value = (row[col.key] ?? "") as ExcelJS.CellValue;
      });
    }
    if (end < rows.length) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload(blob, meta.fileName);
};

export const exportHistoricToPdf = <T extends { id: string }>(
  rows: T[],
  columns: ReportTableColumn<T>[],
  meta: ExportMeta,
): void => {
  const doc = new jsPDF({ orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(16);
  doc.setTextColor(...BRAND_RGB);
  doc.text(meta.title, pageWidth / 2, 14, { align: "center" });

  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(meta.period, pageWidth / 2, 21, { align: "center" });

  autoTable(doc, {
    startY: 26,
    head: [columns.map((c) => c.header)],
    body: rows.map((row) => columns.map((c) => String(row[c.key] ?? ""))),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: BRAND_RGB, textColor: [255, 255, 255], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  doc.save(meta.fileName);
};
